import React, { useState, useEffect } from 'react'
import { ArrowLeft, Send, CheckCircle2, Image as ImageIcon, Calendar, Trash2, Users, Clock } from 'lucide-react'
import { collection, addDoc, serverTimestamp, getDocs, query, where, deleteDoc, doc, orderBy, Timestamp } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage'
import { db } from '../../firebaseConfig'

const storage = getStorage()

export default function NotifDriverMobilPage({ onBack }) {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [successStatus, setSuccessStatus] = useState(false)

  // --- TAMBAHAN STATE BARU ---
  const [drivers, setDrivers] = useState([])
  const [selectedDrivers, setSelectedDrivers] = useState([])
  const [imageFile, setImageFile] = useState(null)
  const [scheduleTime, setScheduleTime] = useState('')
  const [history, setHistory] = useState([])

  useEffect(() => {
    fetchDrivers()
    fetchHistory()
  }, [])

  const fetchDrivers = async () => {
    const q = query(collection(db, 'drivers'), where('status', '==', 'Aktif'))
    const snap = await getDocs(q)
    const list = snap.docs.map(d => ({ id: d.id, name: d.data().name || 'Driver Zimo', service: d.data().service || '' }))
    setDrivers(list)
  }

  const fetchHistory = async () => {
    // Membaca riwayat khusus untuk target "Driver Mobil"
    const q = query(collection(db, 'notifications'), where('target', '==', 'Driver Mobil'), orderBy('timestamp', 'desc'))
    const snap = await getDocs(q)
    setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  }

  const handleDeleteHistory = async (id) => {
    if (window.confirm('Yakin ingin menghapus notifikasi ini? Ini akan menarik notifikasi dari HP Driver.')) {
      await deleteDoc(doc(db, 'notifications', id))
      fetchHistory() // Refresh list
    }
  }

  const handleSendNotification = async (e) => {
    e.preventDefault()
    if (!title.trim() || !message.trim()) {
      alert('Judul dan isi notifikasi tidak boleh kosong!')
      return
    }

    setIsSending(true)
    try {
      // 1. Upload Gambar jika ada
      let imageUrl = null
      if (imageFile) {
        const storageRef = ref(storage, `notifications/${Date.now()}_${imageFile.name}`)
        const snap = await uploadBytes(storageRef, imageFile)
        imageUrl = await getDownloadURL(snap.ref)
      }

      // 2. Set Waktu (Sekarang atau Terjadwal)
      let timeToSend = serverTimestamp()
      if (scheduleTime) {
        timeToSend = Timestamp.fromDate(new Date(scheduleTime))
      }

      // 3. Simpan ke koleksi 'notifications' di Firestore
      await addDoc(collection(db, 'notifications'), {
        title: title.trim(),
        message: message.trim(),
        target: 'Driver Mobil', // Khusus untuk Driver Mobil
        targetUsers: selectedDrivers.length > 0 ? selectedDrivers : ['ALL'],
        imageUrl: imageUrl, 
        timestamp: timeToSend,
        sender: 'Admin Zimo',
      })

      setSuccessStatus(true)
      setTitle('')
      setMessage('')
      setImageFile(null)
      setScheduleTime('')
      setSelectedDrivers([])
      fetchHistory() // Refresh riwayat agar langsung muncul
      
      // Hilangkan pesan sukses setelah 3 detik
      setTimeout(() => setSuccessStatus(false), 3000)
    } catch (error) {
      console.error('Error sending notification:', error)
      alert('Gagal mengirim notifikasi. Periksa koneksi atau aturan Firebase Anda.')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 shadow-lg min-h-[60vh] relative">
      {/* Tombol Back */}
      <button 
        onClick={onBack}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition mb-6"
      >
        <ArrowLeft size={20} />
        Kembali
      </button>

      <div className="mb-8 border-b border-gray-800 pb-4">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          🚗 Kirim Notifikasi (ZiMobil & Extra)
        </h2>
        <p className="text-gray-400 text-sm mt-1">
          Notifikasi ini akan muncul seketika di HP seluruh mitra driver mobil yang aktif.
        </p>
      </div>

      <form onSubmit={handleSendNotification} className="space-y-5 max-w-2xl">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Judul Notifikasi</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Contoh: Info Ganjil Genap, Bonus Akhir Pekan..."
            className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Isi Pesan</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tuliskan detail informasi di sini..."
            rows="5"
            className="w-full bg-gray-950 border border-gray-700 rounded-lg p-3 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition resize-y"
          />
        </div>

        {/* --- TAMBAHAN FITUR: PILIH DRIVER SPESIFIK --- */}
        <div className="bg-gray-950 border border-gray-800 p-4 rounded-lg">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
            <Users size={16} /> Pilih Driver Tertentu (Abaikan jika ingin Broadcast ke Semua)
          </label>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {drivers.length === 0 ? <p className="text-xs text-gray-500">Memuat driver...</p> : drivers.map(d => (
              <label key={d.id} className="flex items-center gap-3 p-2 hover:bg-gray-900 rounded cursor-pointer transition">
                <input 
                  type="checkbox" 
                  checked={selectedDrivers.includes(d.id)} 
                  onChange={(e) => {
                    if (e.target.checked) setSelectedDrivers([...selectedDrivers, d.id])
                    else setSelectedDrivers(selectedDrivers.filter(id => id !== d.id))
                  }} 
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800" 
                />
                <span className="text-sm text-gray-300">{d.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* --- TAMBAHAN FITUR: GAMBAR & JADWAL --- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2"><ImageIcon size={16} /> Gambar Lampiran</label>
            <input 
              type="file" 
              accept="image/*" 
              onChange={(e) => setImageFile(e.target.files[0])} 
              className="w-full bg-gray-950 border border-gray-800 rounded-lg p-2 text-sm text-gray-400 file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:bg-gray-800 file:text-gray-300 hover:file:bg-gray-700 transition cursor-pointer" 
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2"><Calendar size={16} /> Jadwal Kirim</label>
            <input 
              type="datetime-local" 
              value={scheduleTime} 
              onChange={(e) => setScheduleTime(e.target.value)} 
              className="w-full bg-gray-950 border border-gray-800 rounded-lg p-2.5 text-sm text-gray-300 focus:border-blue-500 outline-none" 
              style={{ colorScheme: 'dark' }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSending}
          className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-900/20"
        >
          {isSending ? (
            'Mengirim...'
          ) : (
            <>
              <Send size={18} />
              Kirim Notifikasi Sekarang
            </>
          )}
        </button>

        {/* Indikator Sukses */}
        {successStatus && (
          <div className="inline-flex items-center gap-2 mt-4 text-blue-400 bg-blue-900/20 p-3 rounded-lg border border-blue-900/50">
            <CheckCircle2 size={20} />
            <span>Notifikasi berhasil disiarkan!</span>
          </div>
        )}
      </form>

      {/* --- TAMBAHAN KODE: RIWAYAT NOTIFIKASI --- */}
      <div className="mt-12 pt-8 border-t border-gray-800">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Clock size={18} className="text-blue-500" /> Riwayat & Notif Terjadwal
        </h3>
        <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
          {history.length === 0 ? (
            <p className="text-sm text-gray-500">Belum ada riwayat notifikasi.</p>
          ) : (
            history.map((h) => (
              <div key={h.id} className="flex justify-between items-start bg-gray-950 p-4 rounded-xl border border-gray-800 hover:border-gray-700 transition">
                <div className="flex-1 pr-4">
                  <p className="font-semibold text-gray-200 text-sm flex items-center gap-2">
                    {h.title}
                    {h.imageUrl && <ImageIcon size={12} className="text-blue-400" title="Ada Lampiran Gambar" />}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{h.message}</p>
                  <div className="flex flex-wrap gap-3 mt-3 text-[10px] font-medium text-gray-400">
                    <span className="bg-gray-800 px-2 py-1 rounded">
                      {h.timestamp?.toDate ? h.timestamp.toDate().toLocaleString('id-ID') : 'Segera'}
                    </span>
                    {h.targetUsers?.includes('ALL') 
                      ? <span className="bg-blue-900/30 text-blue-400 px-2 py-1 rounded border border-blue-900/50">Kirim ke: SEMUA</span> 
                      : <span className="bg-indigo-900/30 text-indigo-400 px-2 py-1 rounded border border-indigo-900/50">Kirim ke: {h.targetUsers?.length || 0} Driver Spesifik</span>
                    }
                  </div>
                </div>
                <button onClick={() => handleDeleteHistory(h.id)} className="p-2 text-red-500 bg-gray-900 hover:bg-red-500 hover:text-white rounded-lg transition border border-gray-800 hover:border-red-500" title="Hapus / Batalkan Notifikasi">
                  <Trash2 size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}